import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { couponAPI } from '../../services/api';
import api from '../../services/api';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Link,
    Paper,
    Collapse,
    FormControlLabel,
    Checkbox
} from '@mui/material';

const validationSchema = Yup.object({
    username: Yup.string()
        .min(3, 'Username must be at least 3 characters')
        .max(30, 'Username must be less than 30 characters')
        .matches(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores and hyphens')
        .required('Username is required'),
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]/,
            'Password must contain at least one letter, one number and one special character'
        )
        .required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm password is required')
});

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { register, refreshUserData } = useAuth();
    const [error, setError] = useState('');
    const [emailSuggestion, setEmailSuggestion] = useState('');
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [showCoupon, setShowCoupon] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [marketingConsent, setMarketingConsent] = useState(false);

    // Extract subscription information from URL parameters
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const planType = queryParams.get('planType');
        const billingCycle = queryParams.get('billingCycle');
        
        if (planType && billingCycle) {
            setSubscriptionPlan({
                planType,
                billingCycle,
                teamSize: queryParams.get('teamSize') ? parseInt(queryParams.get('teamSize')) : null
            });
        }
    }, [location]);

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        
        try {
            // Collect attribution data from URL params and localStorage
            const queryParams = new URLSearchParams(location.search);
            const utmSource = queryParams.get('utm_source') || localStorage.getItem('rf_utm_source') || null;
            const utmCampaign = queryParams.get('utm_campaign') || localStorage.getItem('rf_utm_campaign') || null;
            const fromSource = queryParams.get('from') || localStorage.getItem('rf_source') || null;
            const refCode = queryParams.get('ref') || null;
            const attribution = (utmSource || utmCampaign || fromSource || refCode) ? {
                firstTouchSource: fromSource || utmSource || (refCode ? 'challenge' : 'direct'),
                firstTouchCampaign: utmCampaign,
                lastTouchSource: fromSource || utmSource || (refCode ? 'challenge' : 'direct'),
                lastTouchCampaign: utmCampaign,
                conversionSource: fromSource || utmSource || (refCode ? 'challenge' : 'direct'),
            } : null;

            await register(values.username, values.email, values.password, { marketingConsent, attribution });

            // Merge guest assessment if coming from speed test
            const guestId = queryParams.get('guestId');
            if (guestId) {
                try {
                    await api.post('/speed-assessment/merge', { guestId });
                } catch (_) {}
                try {
                    await api.post('/challenges/merge-guest', { guestId });
                } catch (_) {}
            }

            // If a coupon code was entered, try to redeem it
            if (couponCode.trim()) {
                try {
                    await couponAPI.redeem(couponCode.trim());
                    await refreshUserData();
                    navigate('/dashboard');
                    return;
                } catch (couponErr) {
                    // Registration succeeded but coupon failed — still navigate, show nothing
                    // The user can try the coupon later or it was invalid
                }
            }
            
            // If a subscription plan was selected, redirect to checkout
            if (subscriptionPlan) {
                navigate(`/subscription?planType=${subscriptionPlan.planType}&billingCycle=${subscriptionPlan.billingCycle}${subscriptionPlan.teamSize ? `&teamSize=${subscriptionPlan.teamSize}` : ''}&registered=true`);
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            const data = err.response?.data;
            setEmailSuggestion(data?.emailSuggestion || '');
            if (data?.errors && Array.isArray(data.errors)) {
                const emailErr = data.errors.find(e => e.path === 'email');
                setError(emailErr?.msg || data.errors[0]?.msg || 'Registration failed');
            } else {
                setError(data?.error || 'Registration failed');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                    <Typography component="h1" variant="h5" align="center" gutterBottom>
                        Create Your ReaderFLEX Account
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {emailSuggestion && (
                        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setEmailSuggestion('')}>
                            Did you mean <strong>{emailSuggestion}</strong>?
                        </Alert>
                    )}

                    <Formik
                        initialValues={{
                            username: '',
                            email: '',
                            password: '',
                            confirmPassword: ''
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                            <Form>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="username"
                                    label="Username"
                                    value={values.username}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.username && Boolean(errors.username)}
                                    helperText={touched.username && errors.username}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="password"
                                    label="Password"
                                    type="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={marketingConsent}
                                            onChange={(e) => setMarketingConsent(e.target.checked)}
                                            name="marketingConsent"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" color="text.secondary">
                                            Send me reading tips, progress updates, and product education emails. I can unsubscribe at any time.
                                        </Typography>
                                    }
                                    sx={{ mt: 1, mb: 1, alignItems: 'flex-start' }}
                                />

                                <Box sx={{ mt: 2, textAlign: 'right' }}>
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        underline="hover"
                                        sx={{ color: 'text.disabled', fontSize: '0.75rem' }}
                                        onClick={() => setShowCoupon(!showCoupon)}
                                    >
                                        {showCoupon ? 'Hide coupon' : 'Have a coupon code?'}
                                    </Link>
                                </Box>
                                <Collapse in={showCoupon}>
                                    <TextField
                                        fullWidth
                                        margin="dense"
                                        size="small"
                                        label="Coupon Code"
                                        placeholder="e.g. aX9#-kP2!-mN7$-qR4&"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                    />
                                </Collapse>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                                </Button>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Link href="/login" variant="body2">
                                        Already have an account? Sign in
                                    </Link>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Box>
        </Container>
    );
};

export default Register;
